import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { AppUser } from '@/lib/auth';
import {
  getLeadDetailForUser,
  listLeadsForUser,
  listLeadsByPhoneForUser,
  listLeadTimelineEventsForUser,
  type GetLeadDetailArgs,
  type ListLeadsArgs,
  type ListLeadsByPhoneArgs,
  type ListLeadTimelineEventsArgs,
} from '@/lib/services/leads';
import type { LeadDetail, LeadSummary, LeadListFilters, LeadTimelineEvent } from '@/lib/types/lead';
import type { PaginatedResult } from '@/lib/types/property';
import { normalizePhoneNumber } from '@/lib/utils/phone';

export interface UseLeadsListOptions {
  user: AppUser;
  page: number;
  pageSize: number;
  filters?: LeadListFilters;
}

export function useLeadsList({
  user,
  page,
  pageSize,
  filters,
}: UseLeadsListOptions): UseQueryResult<PaginatedResult<LeadSummary>> {
  const payload = useMemo<ListLeadsArgs>(
    () => ({
      user,
      page,
      pageSize,
      filters,
    }),
    [filters, page, pageSize, user],
  );

  return useQuery<PaginatedResult<LeadSummary>>({
    queryKey: ['leads', { userId: user.id, page, pageSize, filters }],
    queryFn: async () => {
      return await listLeadsForUser(payload);
    },
    staleTime: 60 * 1000,
  });
}

export interface UseLeadsByPhoneOptions {
  user: AppUser;
  phone: string | null;
  limit?: number;
}

export function useLeadsByPhone({
  user,
  phone,
  limit,
}: UseLeadsByPhoneOptions): UseQueryResult<LeadSummary[]> {
  const payload = useMemo<ListLeadsByPhoneArgs | null>(() => {
    const normalizedPhone = normalizePhoneNumber(phone);
    if (!normalizedPhone) {
      return null;
    }
    return {
      user,
      phone: normalizedPhone,
      limit,
    };
  }, [limit, phone, user]);

  return useQuery<LeadSummary[]>({
    queryKey: ['leads-by-phone', { userId: user.id, phone: payload?.phone ?? null, limit }],
    enabled: payload !== null,
    queryFn: async () => {
      if (!payload) {
        throw new Error('Missing phone number.');
      }
      return await listLeadsByPhoneForUser(payload);
    },
    staleTime: 60 * 1000,
  });
}

export interface UseLeadDetailOptions {
  user: AppUser;
  leadId: string | null;
}

export function useLeadDetail({ user, leadId }: UseLeadDetailOptions): UseQueryResult<LeadDetail> {
  const payload = useMemo<GetLeadDetailArgs | null>(() => {
    const normalizedId = typeof leadId === 'string' ? leadId.trim() : '';
    if (normalizedId.length === 0) {
      return null;
    }
    return {
      user,
      leadId: normalizedId,
    };
  }, [leadId, user]);

  return useQuery<LeadDetail>({
    queryKey: ['lead-detail', { userId: user.id, leadId }],
    enabled: payload !== null,
    queryFn: async () => {
      if (!payload) {
        throw new Error('Missing lead identifier.');
      }
      return await getLeadDetailForUser(payload);
    },
    staleTime: 60 * 1000,
  });
}

export interface UseLeadTimelineOptions {
  user: AppUser;
  leadId: string | null;
  limit?: number;
}

export function useLeadTimeline({
  user,
  leadId,
  limit,
}: UseLeadTimelineOptions): UseQueryResult<LeadTimelineEvent[]> {
  const payload = useMemo<ListLeadTimelineEventsArgs | null>(() => {
    const normalizedId = typeof leadId === 'string' ? leadId.trim() : '';
    if (normalizedId.length === 0) {
      return null;
    }

    return {
      user,
      leadId: normalizedId,
      limit,
    };
  }, [leadId, limit, user]);

  return useQuery<LeadTimelineEvent[]>({
    queryKey: ['lead-timeline', { userId: user.id, leadId, limit }],
    enabled: payload !== null,
    queryFn: async () => {
      if (!payload) {
        throw new Error('Missing lead identifier.');
      }
      return await listLeadTimelineEventsForUser(payload);
    },
    staleTime: 30 * 1000,
  });
}
